import type React from 'react';
import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import Typography from 'app/ui/components/atoms/typography';

import ChevronDownIcon from 'app/assets/chevron-down-icon.svg';
import ChevronUpIcon from 'app/assets/chevron-up-icon.svg';

export type Option = {
  id: string;
  name: string;
};

export type DropDownVariant = 'light' | 'dark';

export type DropDownProps = {
  options: Option[];
  disabled: boolean;
  onChange: (selectedOption: Option) => void;
  noValueText: string;
  value?: string;
  variant?: DropDownVariant;
};

const DropDownContainer = styled.div`
  width: 100%;
  margin: 0 auto;
`;

const DropDownHeader = styled.div<{ disabled: boolean; $variant: DropDownVariant }>`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  ${({ theme: { spacing, colors }, disabled, $variant }) => `
    cursor: ${disabled ? 'not-allowed' : 'pointer'};
    margin-bottom: ${spacing(2)};
    padding: ${spacing(3)} ${spacing(5)} ${spacing(3)} ${spacing(4)};
    border-radius: ${spacing(2)};
    border: 0.0625rem solid ${$variant === 'dark' ? colors.blackSwan : 'transparent'};
    background: ${disabled ? colors.veryGray : $variant === 'dark' ? colors.black : colors.white};
  `}

  img {
    ${({ $variant }) => ($variant === 'dark' ? 'filter: invert(1);' : '')}
  }
`;

const DropDownList = styled.ul<{ $variant: DropDownVariant }>`
  margin: 0;
  padding: 0;
  border-radius: ${({ theme: { spacing } }) => spacing(2)};
  overflow: hidden;
  ${({ theme: { colors }, $variant }) => ($variant === 'dark' ? `border: 0.0625rem solid ${colors.blackSwan};` : '')}
`;

const ListItem = styled.li<{ $variant: DropDownVariant }>`
  list-style: none;
  cursor: pointer;
  ${({ theme: { colors, spacing }, $variant }) => `
    background: ${$variant === 'dark' ? colors.black : colors.white};
    padding: ${spacing(2)} 0 ${spacing(2)} ${spacing(4)};
    border-bottom: 1px solid ${$variant === 'dark' ? colors.blackSwan : colors.black};
    &:first-child {
      border-top-left-radius: ${spacing(2)};
      border-top-right-radius: ${spacing(2)};
    }
    &:last-child {
      border-bottom-left-radius: ${spacing(2)};
      border-bottom-right-radius: ${spacing(2)};
    }
    &:hover {
      background: ${$variant === 'dark' ? colors.veryGray : colors.paragraphGray};
    }
  `}

`;

const DropDown: React.FC<DropDownProps> = ({
  options,
  disabled = false,
  onChange,
  value,
  noValueText,
  variant = 'light',
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const toggling = () => (disabled ? null : setIsOpen(!isOpen));

  const onClick = (option: Option) => {
    toggling();
    onChange(option);
  };

  return (
    <DropDownContainer>
      <DropDownHeader onClick={toggling} disabled={disabled} $variant={variant}>
        {value ? (
          <Typography
            variant="body2"
            color={disabled ? theme.colors.lightGray : variant === 'dark' ? theme.colors.white : theme.colors.veryGray}
          >
            {value}
          </Typography>
        ) : (
          <Typography variant="body2" color={theme.colors.lightGray}>
            {noValueText}
          </Typography>
        )}
        {isOpen ? (
          <img src={ChevronUpIcon} alt="Chevron Up Icon" />
        ) : (
          <img src={ChevronDownIcon} alt="Chevron Down Icon" />
        )}
      </DropDownHeader>
      {isOpen && (
        <DropDownList $variant={variant}>
          {options.map((option) => (
            <ListItem id={option.id} onClick={() => onClick(option)} key={option.name} $variant={variant}>
              <Typography variant="body2" color={variant === 'dark' ? theme.colors.white : theme.colors.veryGray}>
                {option.name}
              </Typography>
            </ListItem>
          ))}
        </DropDownList>
      )}
    </DropDownContainer>
  );
};

export default DropDown;
